<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Floorplans</h2>
    </div>

    <div class="flex w-full flex-col flex-wrap md:flex-row md:justify-start gap-4 mt-8">
        <div class="mb-4 flex-initial min-w-80">
          <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
          <SearchInput v-model="query.search" class="min-w-100" color="gray" placeholder="Search floorplans by keyword" />
        </div>
        <div v-if="collections.length > 1" class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.collections" label="Collections" :options="collections.map(collection => ( { label: collection.meta.info.name, value: collection.id } ))" empty="All Collections" all="All Collections" />
        </div>
        <div class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.status" label="Published" :options="Statuses.statuses" all="All Statuses"/>
        </div>
        <div class="mb-4 flex-initial">
            <SelectMultipleField v-model="query.saleStatus" label="Sales Status" :options="FloorplanStatuses.statuses" all="All Sales Statuses" empty="All Sales Statuses"/>
        </div>
        <div v-if="hasFilters" class="mb-4 flex-initial sm:mt-5">
            <FormButton @click="clear" color="blue">Clear Filters</FormButton>
        </div>
        <div class="mb-4 flex-initial sm:mt-6 ml-auto">
            <button @click="showColumns = !showColumns" class="w-9 h-9 items-center justify-center bg-transparent hover:bg-bhav-cool-gray-50 text-bhav-blue-200 hover:text-bhav-blue-500 rounded-lg text-lg">
                <p class="relative">
                    <span class="fak fa-col-options leading-none"></span>
                    <span class="sr-only">Open Table Column Preferences Drawer</span>
                </p>
            </button>
        </div>
    </div>

    <PaginationTable empty-message="There are currently no floorplan items." :data="filtered" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false" :show-columns="showColumns" @closeDrawer="showColumns = false">

        <template #col-name="{ row }">
            <router-link :to="link(row)" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-600 whitespace-nowrap">
                {{ row.meta?.info?.name || row.kova?.MarketingName }}
            </router-link>
        </template>

        <template #col-collection="{ row }">
            <router-link :to="{name: 'collection-show', params: {id: row.collection?.id }}" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-600 whitespace-nowrap">
                {{ row.collection?.meta?.info?.name || row.collection?.kova?.MarketingName }}
            </router-link>
        </template>

        <template #col-modelid="{ row }">
            <router-link :to="{name: 'collection-show', params: {id: row.collection?.id }}" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-600 whitespace-nowrap">
                {{ row.kova?.ModelID }}
            </router-link>
        </template>

        <template #col-price="{ row }">
            <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            <div class="text-sm leading-5 text-bhav-cool-gray-500">
                {{ formatToCurrency(row.meta?.info?.price?.min || row.kova?.Price) }}
            </div>
            </router-link>
        </template>

        <template #col-status="{ row }">
            <router-link :to="link(row)" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-600 whitespace-nowrap">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="['Pre-Sale (VIP)', 'Off Market', 'Sold Out', 'Internal', '', 'N/S'].includes(row.kova.CustomFields.TextCommModelStatus1) ? 'bg-oakwood-red-100 text-oakwood-red-800' : 'bg-bhav-green-100 text-oakwood-green-800'">
                    {{ row.kova.CustomFields.TextCommModelStatus1 }}
                </span>
            </router-link>
        </template>

        <template #col-bedrooms="{ row }">
            <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ getRange(row.meta.info.bedrooms.min, row.meta.info.bedrooms.max) }}
            </router-link>
        </template>

        <template #col-bathrooms="{ row }">
            <router-link class="block whitespace-nowrap py-4" :to="link(row)">
                {{ getRange(((row.meta.info.bathrooms.min.full || 0) * 1) + ((row.meta.info.bathrooms.min.half || 0) * .5), ((row.meta.info.bathrooms.max.full || 0) * 1) + ((row.meta.info.bathrooms.max.half || 0) * .5)) }}
            </router-link>
        </template>

        <template #col-garages="{ row }">
            <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ getRange(row.meta.info.garages.min || '-', row.meta.info.garages.max || '-') }}
            </router-link>
        </template>
        
        <template #col-sqft="{ row }">
            <router-link class="block whitespace-nowrap py-4" :to="link(row)">
            {{ getRange(row.meta.info.sqft.min || '-', row.meta.info.sqft.max || '-') }}
            </router-link>
        </template>

        <template #col-published="{ row }">
            <div v-if="GlobalPolicy.edit()" class="py-4 flex gap-2 items-center">
                <ToggleButton :id="`item-${row.id}-status`" @changed="save(row)" :checked="row.status == 'active'" 
                :label="row.status == 'active' ? 'Published' : 'Unpublished'"/>
                <label :for="`item-${row.id}-status`">{{ Statuses.label(row.status) }}</label>
            </div>
            <div v-else class="py-4">
                {{ Statuses.label(row.status) }}
            </div>
        </template>

    </PaginationTable>
       
</template>

<script>
import axios from 'axios';
import {FormButton, SelectField, SelectMultipleField, ToggleButton} from '@/components/form';
import PaginationTable from '@/components/PaginationTable.vue';
import Statuses from '@/enums/Statuses';
import { GlobalPolicy } from '@/policies/global';
import { formatToCurrency, getRange, formatToNumber } from '@/helpers/numberHelpers';
import FloorplanStatuses from '@/enums/FloorplanStatuses';
import SearchInput from '../form/SearchInput.vue';

const query = {
    search: null,
    collections: [],
    saleStatus: [],
    status: ['new','active','inactive']
}

export default {
    setup() {
        return {
            GlobalPolicy,
            Statuses,
            FloorplanStatuses,
            getRange,
            formatToCurrency,
            link: (row) => ({ name: 'floorplan-show', params: { id: row.id } }),
        }
    },
    data() {
        return {
            query: {...query},
            showColumns: false
        }
    },
    emits: [
        'update'
    ],
    props: {
        floorplans: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    methods: {
        save(item) {
            item.status = item.status != 'active' ? 'active' : 'inactive';
            axios.patch(`floorplans/${item.id}`, item)
                .then(res => {
                    if(res.data?.id) {
                        this.$emit('update',res.data);
                    }
                })
                .catch(e => {
                    throw e;
                });
        },
        clear() {
            this.query = {...query};
        }
    },
    computed: {
        filtered() {
            let results = this.floorplans;

            if (this.query.status.length) {
                results = results.filter(item => this.query.status.includes(item.status));
            }

            if (this.query.saleStatus.length) {
                results = results.filter(item => { 
                    console.error('Error', item.kova?.CustomFields?.TextCommModelStatus1)
                    return this.query.saleStatus.includes(item.kova?.CustomFields?.TextCommModelStatus1)});
            }

            if (this.query.collections.length > 0) {
                results = results.filter(item => this.query.collections.includes(item.collection.id));
            }

            if(this.query.search != '') {
                const searchTerm = this.query.search?.toLowerCase() || '';
                results = results.filter(item => {
                    const name = (item.meta?.info?.name || item.kova?.MarketingName || '').toLowerCase();
                    const description = (item.meta?.info?.description || '').toLowerCase();
                    const collectionName = (item.collection?.meta?.info?.name || item.collection?.kova?.MarketingName || '').toLowerCase();
                    const price = (formatToCurrency(item.meta?.info?.price?.min || item.kova?.Price || '')).toString().toLowerCase();
                    const bedrooms = (getRange(item.meta?.info?.bedrooms?.min, item.meta?.info?.bedrooms?.max) || '').toString().toLowerCase();
                    const bathrooms = (getRange(((item.meta?.info?.bathrooms?.min?.full || 0) * 1) + ((item.meta?.info?.bathrooms?.min?.half || 0) * .5), ((item.meta?.info?.bathrooms?.max?.full || 0) * 1) + ((item.meta?.info?.bathrooms?.max?.half || 0) * .5)) || '').toString().toLowerCase();
                    const garages = (getRange(item.meta?.info?.garages?.min || '-', item.meta?.info?.garages?.max || '-') || '').toString().toLowerCase();
                    const sqft = (getRange(item.meta?.info?.sqft?.min || '-', item.meta?.info?.sqft?.max || '-') || '').toString().toLowerCase();

                    return name.includes(searchTerm) ||
                           description.includes(searchTerm) ||
                           collectionName.includes(searchTerm) ||
                           price.includes(searchTerm) ||
                           bedrooms.includes(searchTerm) ||
                           bathrooms.includes(searchTerm) ||
                           garages.includes(searchTerm) ||
                           sqft.includes(searchTerm);
                });
            }

            return results;
        },
        collections() {
            if(!this.floorplans[0]?.collection) return [];
            return [...new Set(this.floorplans.map(floorplan => JSON.stringify(floorplan.collection)))].map(floorplan => JSON.parse(floorplan))
        },
        hasFilters() {
            return JSON.stringify(query) !== JSON.stringify(this.query);
        },
        columns() {
            let columns = [
              {
                name: 'name',
                label: 'Name',
                sort: (row) => row.meta.info?.name || row.kova?.MarketingName || "",
                default: true
              },
              {
                name: 'modelid',
                label: 'Model ID',
                sort: (row) => row.kova?.ModelID || "",
                default: true
              },
              {
                name: 'price',
                label: 'Base Price',
                sort: (row) => row.meta.info?.price?.min || row.kova?.Price || 0,
                default: false
              },
              {
                name: 'status',
                label: 'Sales Status',
                sort: (row) => FloorplanStatuses.label(row.kova.CustomFields.TextCommModelStatus1),
                default: true
              },
              {
                name: 'bedrooms',
                label: 'Bedrooms',
                sort: (row) => row.meta.info.bedrooms.min || 0,
                default: false
              },
              {
                name: 'bathrooms',
                label: 'Bathrooms',
                sort: (row) => (row.meta.info.bathrooms.min.full || 0) + ((row.meta.info.bathrooms.min.half || 0) * .5),
                default: false
              },
              {
                name: 'garages',
                label: 'Garages',
                sort: (row) => row.meta.info.garages.min || 0,
                default: false
              },
              {
                name: 'sqft',
                label: 'Square Footage',
                sort: (row) => row.meta.info.sqft.min || 0,
                default: false
              },
              {
                name: 'published',
                label: 'Published',
                sort: (row) => Statuses.label(row.status),
                default: true
              }
            ];

            if(this.collections?.length ?? 0 > 0) {
                columns.splice(
                    1,
                    0,
                    {
                        name: 'collection',
                        label: 'Collection',
                        sort: (row) => row.collection?.meta?.info?.name || row.collection?.kova?.MarketingName,
                        default: false
                    }
                )
            }
            return columns;
        }
    },
    components: {
        PaginationTable,
        ToggleButton,
        SelectMultipleField,
        SelectField,
        FormButton,
        SearchInput
    }
}
</script>@/components/form